import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subscription } from 'rxjs';

import { RoutingState, UpdateRecentRoutes } from '@shared';
import * as accountUserActions from '@account-users/account/state/account-user/account-user.actions';
import * as fromAccountUser from '@account-users/account/state/account-user/account-user.reducer';
import * as sectionsActions from '@account-users/account/state/account-sections/account-sections.actions';
import * as fromSections from '@account-users/account/state/account-sections/account-sections.reducer';

import { IAccountUser } from '@account-users/account/models/IAccountUser';
import { AuthService } from '@account-users/auth/auth.service';
import { IToolInfoUpdate } from '@account-users/account/models/IToolInfo';
import { AccountUserType } from '@account-users/account/models/AccountUserType';
import {
  UserLicencePermission,
  LicensePermission,
} from '@account-users/account/models/LicensePermissionList';
import { License } from '@account-users/account/models/License';
import { LicenseTypeConfig } from '@account-users/account/models/LicenseTypeConfig';
import { AccountLicense } from '@account-users/account/models/AccountLicense';
import { Permission } from '@account-users/account/models/Permission';
import { Title } from '@angular/platform-browser';
import { Store, select } from '@ngrx/store';
import { RoleType, IUserRole } from '@account-users/user/models/UserRole';
import { UserRolesAndPermissionsService } from '@account-users/user/services/user-roles-and-permissions.service';
import { ISection } from '@account-users/account/models/Audience';

@Component({
  selector: 'app-account-user',
  templateUrl: './account-user.component.html',
  styleUrls: ['./account-user.component.scss'],
})
export class AccountUserComponent implements OnInit, OnDestroy {
  public user: IAccountUser;
  public accountUsers: IAccountUser[];
  public user$: Observable<IAccountUser> = this.accountUserStore.pipe(
    select(fromAccountUser.getAccountUser),
  );

  public accountUsers$: Observable<IAccountUser[]> = this.accountUserStore.pipe(
    select(fromAccountUser.getAccountUsers),
  );
  public licensePermissionsForAccountUser$: Observable<
    UserLicencePermission[]
  > = this.accountUserStore.pipe(
    select(fromAccountUser.getAccountUserLicensePermissions),
  );

  public allLicenses$: Observable<License[]> = this.accountUserStore.pipe(
    select(fromAccountUser.getAllLicenses),
  );

  public licensePermissions$: Observable<
    LicensePermission[]
  > = this.accountUserStore.pipe(select(fromAccountUser.getLicensePermissions));

  public accountLicenses$: Observable<
    AccountLicense[]
  > = this.accountUserStore.pipe(select(fromAccountUser.getAccountLicenses));

  public licenseTypes$: Observable<
    LicenseTypeConfig[]
  > = this.accountUserStore.pipe(select(fromAccountUser.getLicenseTypes));

  public roles$: Observable<IUserRole[]> = this.accountUserStore.pipe(
    select(fromAccountUser.getUserRoles),
  );

  public isUpdatingLicenseTool$: Observable<
    boolean
  > = this.accountUserStore.pipe(
    select(fromAccountUser.getIsLoadingAccountUserPermissions),
  );
  public allSections$: Observable<ISection[]> = this.accountUserStore.pipe(
    select(fromSections.getAccountSectionsAsArray),
  );
  public isUpdatingSections$: Observable<boolean> = this.accountUserStore.pipe(
    select(fromSections.getIsLoadingAccountSections),
  );

  public isLoading$: Observable<boolean> = this.accountUserStore.pipe(
    select(fromAccountUser.getIsLoading),
  );

  public isError$: Observable<boolean> = this.accountUserStore.pipe(
    select(fromAccountUser.getIsError),
  );

  public isUpdatingAccountRole$: Observable<
    boolean
  > = this.accountUserStore.pipe(select(fromAccountUser.getIsUpdatingRole));

  public isDataLoaded$: Observable<boolean> = this.accountUserStore.pipe(
    select(fromAccountUser.isDataLoaded),
  );

  public hasOnlyTrialDispenserRole$ = this.userRolesService
    .hasOnlyTrialDispenserRole$;

  public AccountUserType = AccountUserType;
  public roles: IUserRole[];
  public editRoles = false;
  private subscriptions: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private auth: AuthService,
    private pageTitle: Title,
    private routingStore: Store<RoutingState>,
    private accountUserStore: Store<
      fromAccountUser.AccountUserState | fromSections.AccountSectionsState
    >,
    private userRolesService: UserRolesAndPermissionsService,
  ) {}

  ngOnInit() {
    console.log('AccountUserComponent');

    this.accountUserStore.dispatch(
      new accountUserActions.LoadAllAccountUserLicensesAction({
        accountId: this.accountId,
        userId: this.userId,
      }),
    );

    this.accountUserStore.dispatch(
      new sectionsActions.LoadSections({ accountId: this.accountId }),
    );

    this.user$.subscribe((user) => {
      this.user = user;
      if (user) {
        this.pageTitle.setTitle(
          `${user.name} on ${this.accountId} | One Back Office`,
        );
        this.routingStore.dispatch(
          new UpdateRecentRoutes({
            id: 0,
            type: 'Permissions',
            title: `${user.name} on ${this.accountId}`,
            url: location.href,
          }),
        );
      }
    });

    this.subscriptions = [
      this.roles$.subscribe((roles) => (this.roles = roles)),
      this.accountUsers$.subscribe((users) => (this.accountUsers = users)),
    ];
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  get userId() {
    return this.route.snapshot.paramMap.get('userId');
  }

  get accountId() {
    return this.route.snapshot.paramMap.get('accountId');
  }

  get canTogglePermissions() {
    const boUser = this.auth.profile;
    return (
      boUser.permissions.includes(Permission.CanPlaceOrders) ||
      this.accountUsers.some((u) => u.email === boUser.email)
    );
  }

  get accountUserTypeRoles() {
    if (this.user && this.user.roles && this.user.roles.length) {
      return this.user.roles.filter((role) =>
        this.roles.find((r) => r.code === role),
      );
    } else {
      return [];
    }
  }

  updatePermission(toolUpdate: IToolInfoUpdate) {
    this.accountUserStore.dispatch(
      new accountUserActions.UpdateAccountUserLicensePermissions({
        accountId: this.accountId,
        userId: this.userId,
        toolUpdate,
      }),
    );
  }

  updateSectionPermission({ toggle, sectionId }) {
    const method = toggle ? 'put' : 'delete';
    this.accountUserStore.dispatch(
      new accountUserActions.UpdateAccountUserSectionPermissions({
        method,
        accountId: this.accountId,
        userId: this.userId,
        sectionId,
      }),
    );
  }

  onUpdateAccountRoles(update) {
    this.accountUserStore.dispatch(
      new accountUserActions.UpdateAccountUserRolesAction({
        role: update.role.code,
        userId: this.user.userId,
        accountId: this.accountId,
        toggle: update.e,
      }),
    );
  }
}
